
/**
 * Module dependencies.
 */

import { NavThemeProps, navTransition, setNavColor } from './nav-theme';
import { ifProp } from 'styled-tools';
import { units } from 'src/styles/utils';
import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';

/**
 * Constants.
 */

const duration = '0.3s';
const bezier = 'cubic-bezier(0.23, 1, 0.32, 1)';

/**
 * `Props` type.
 */

type Props = NavThemeProps & {
  className?: string,
  isOpen: boolean,
  onClick: () => void
};

/**
 * `Button` styled component.
 */

const Button = styled.button<NavThemeProps & { active: boolean }>`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  display: flex;
  height: ${units(4)};
  justify-content: center;
  outline: none;
  padding: 0;
  transition: ${navTransition};
  transition-property: color, opacity;
  width: ${units(4)};

  ${setNavColor}

  &:focus-visible,
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * `Lines` styled component.
 */

const Lines = styled.span<{ active: boolean }>`
  position: relative;
  transition: background-color 0.3s ease;

  &,
  &::before,
  &::after {
    background-color: currentcolor;
    height: 1.6px;
    width: ${units(3)};
  }

  &::before,
  &::after {
    content: '';
    left: 0;
    position: absolute;
  }

  &::before {
    bottom: ${units(1)};
    transition:
      bottom ${duration} ${duration} ${bezier},
      transform ${duration} ${bezier};
  }

  &::after {
    top: ${units(1)};
    transition:
      top ${duration} ${duration} ${bezier},
      transform ${duration} ${bezier};
  }

  ${ifProp('active', css`
    background-color: transparent !important;

    &::before {
      bottom: 0;
      transform: rotate(-45deg);
      transition:
        bottom ${duration} ${bezier},
        transform ${duration} ${duration} ${bezier};
    }

    &::after {
      top: 0;
      transform: rotate(45deg);
      transition:
        top ${duration} ${bezier},
        transform ${duration} ${duration} ${bezier};
    }
  `)}
`;

/**
 * `HamburgerMenu` component.
 */

const HamburgerMenu = (props: Props): ReactElement => {
  const { className, colorTheme, isOpen, onClick } = props;

  return (
    <Button
      active={isOpen}
      aria-expanded={isOpen}
      aria-label={'Menu'}
      className={className}
      colorTheme={colorTheme}
      onClick={onClick}
      type={'button'}
    >
      <Lines active={isOpen} />
    </Button>
  );
};

/**
 * Default props.
 */

HamburgerMenu.defaultProps = {
  colorTheme: 'dark'
};

/**
 * Export `HamburgerMenu` component.
 */

export default HamburgerMenu;
